import { useEffect, useMemo, useState } from "react";
import { useAuth, useReverification, useUser } from "@clerk/react";
import type {
  EmailAddressResource,
  SessionWithActivitiesResource,
} from "@clerk/shared/types";
import {
  LogOutIcon,
  MailIcon,
  MonitorIcon,
  MoreVerticalIcon,
  PlusIcon,
  ShieldCheckIcon,
  SmartphoneIcon,
  StarIcon,
  Trash2Icon,
} from "lucide-react";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Frame, FrameHeader, FrameTitle, FrameAction, FrameCount } from "@/components/ui/frame";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toastClerkError } from "@/lib/clerk-errors";

const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

/** The email addresses on your Clerk user: add one with a code, make it primary, remove it. */
export function EmailAddressesFrame() {
  const { user } = useUser();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [verifying, setVerifying] = useState<EmailAddressResource | null>(null);

  if (!user) return null;

  const primaryId = user.primaryEmailAddressId;
  const emails = [...user.emailAddresses].sort(
    (a, b) => Number(b.id === primaryId) - Number(a.id === primaryId),
  );

  return (
    <Frame>
      <FrameHeader>
        <FrameTitle>
          Email addresses <FrameCount>{emails.length}</FrameCount>
        </FrameTitle>
        <FrameAction>
          <Button
            size="sm"
            onClick={() => {
              setVerifying(null);
              setDialogOpen(true);
            }}
          >
            <PlusIcon />
            Add email
          </Button>
        </FrameAction>
      </FrameHeader>

      <Table variant="card">
        <TableHeader>
          <TableRow>
            <TableHead>Email</TableHead>
            <TableHead className="w-40">Status</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {emails.map((email) => (
            <EmailAddressRow
              key={email.id}
              email={email}
              isPrimary={email.id === primaryId}
              onVerify={() => {
                setVerifying(email);
                setDialogOpen(true);
              }}
            />
          ))}
        </TableBody>
      </Table>

      <EmailVerificationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        pending={verifying}
      />
    </Frame>
  );
}

/** The devices you're signed in on, this one first; sign out of any other. */
export function ActiveDevicesFrame() {
  const { user } = useUser();
  const { sessionId } = useAuth();
  const [sessions, setSessions] = useState<SessionWithActivitiesResource[] | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const revoke = useReverification((session: SessionWithActivitiesResource) =>
    session.revoke(),
  );

  const userId = user?.id;
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    user
      .getSessions()
      .then((all) => {
        if (!cancelled) setSessions(all.filter((s) => s.status === "active"));
      })
      .catch(() => {
        if (!cancelled) setSessions([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // This device first, then most recently active.
  const ordered = useMemo(
    () =>
      sessions
        ? [...sessions].sort((a, b) =>
            a.id === sessionId
              ? -1
              : b.id === sessionId
                ? 1
                : b.lastActiveAt.getTime() - a.lastActiveAt.getTime(),
          )
        : null,
    [sessions, sessionId],
  );

  if (!user) return null;

  const handleRevoke = async (session: SessionWithActivitiesResource) => {
    setRevoking(session.id);
    try {
      await revoke(session);
      setSessions((prev) => prev?.filter((s) => s.id !== session.id) ?? null);
      toast.success("Signed out of that device");
    } catch (error) {
      toastClerkError(error, "Failed to sign out of that device");
    } finally {
      setRevoking(null);
    }
  };

  return (
    <Frame>
      <FrameHeader>
        <FrameTitle>
          Active devices
          {ordered && <FrameCount>{ordered.length}</FrameCount>}
        </FrameTitle>
      </FrameHeader>

      <Table variant="card">
        <TableHeader>
          <TableRow>
            <TableHead>Device</TableHead>
            <TableHead>Location</TableHead>
            <TableHead className="w-44">Last active</TableHead>
            <TableHead className="w-28" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {!ordered ? (
            <TableRow>
              <TableCell colSpan={4}>
                <Skeleton className="h-9 w-full" />
              </TableCell>
            </TableRow>
          ) : (
            ordered.map((session) => (
              <SessionRow
                key={session.id}
                session={session}
                isCurrent={session.id === sessionId}
                revoking={revoking}
                onRevoke={() => void handleRevoke(session)}
              />
            ))
          )}
        </TableBody>
      </Table>
    </Frame>
  );
}

function EmailAddressRow({
  email,
  isPrimary,
  onVerify,
}: {
  email: EmailAddressResource;
  isPrimary: boolean;
  onVerify: () => void;
}) {
  const { user } = useUser();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState<"primary" | "remove" | null>(null);
  const makePrimary = useReverification(() => {
    if (!user) throw new Error("not_logged_in");
    return user.update({ primaryEmailAddressId: email.id });
  });
  const remove = useReverification(() => email.destroy());

  const verified = email.verification.status === "verified";
  const hasActions = !isPrimary || !verified;

  const handleMakePrimary = async () => {
    setBusy("primary");
    try {
      await makePrimary();
      toast.success(`${email.emailAddress} is now your primary email`);
    } catch (error) {
      toastClerkError(error, "Failed to change your primary email");
    } finally {
      setBusy(null);
    }
  };

  const handleRemove = async () => {
    setBusy("remove");
    try {
      await remove();
      toast.success(`Removed ${email.emailAddress}`);
    } catch (error) {
      toastClerkError(error, "Failed to remove the email address");
    } finally {
      setBusy(null);
    }
  };

  return (
    <TableRow data-context-menu>
      <TableCell>
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted [&_svg]:text-muted-foreground">
            <MailIcon className="size-3.5" />
          </div>
          <span className="truncate text-sm leading-normal">{email.emailAddress}</span>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          {isPrimary && <Badge variant="flat">Primary</Badge>}
          {!verified && <Badge variant="warning">Unverified</Badge>}
        </div>
      </TableCell>
      <TableCell>
        {hasActions && (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="muted-ghost"
                  size="icon-sm"
                  aria-label="Email options"
                  disabled={busy !== null}
                >
                  {busy ? <Spinner className="size-3.5" /> : <MoreVerticalIcon />}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!verified && (
                  <DropdownMenuItem onSelect={onVerify}>
                    <ShieldCheckIcon />
                    Verify
                  </DropdownMenuItem>
                )}
                {verified && !isPrimary && (
                  <DropdownMenuItem onSelect={() => void handleMakePrimary()}>
                    <StarIcon />
                    Make primary
                  </DropdownMenuItem>
                )}
                {!isPrimary && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      variant="destructive"
                      onSelect={() => setConfirmOpen(true)}
                    >
                      <Trash2Icon />
                      Remove
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
              <AlertDialogContent className="max-w-md">
                <AlertDialogHeader>
                  <AlertDialogTitle>Remove {email.emailAddress}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    You won't be able to sign in with this address anymore.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => void handleRemove()} variant="destructive">
                    <Trash2Icon />
                    Remove
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

function SessionRow({
  session,
  isCurrent,
  revoking,
  onRevoke,
}: {
  session: SessionWithActivitiesResource;
  isCurrent: boolean;
  revoking: string | null;
  onRevoke: () => void;
}) {
  const activity = session.latestActivity;
  const browser =
    [activity?.browserName, activity?.browserVersion].filter(Boolean).join(" ") ||
    "Unknown browser";
  const location = [activity?.city, activity?.country].filter(Boolean).join(", ");

  return (
    <TableRow>
      <TableCell>
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted [&_svg]:text-muted-foreground">
            {activity?.isMobile ? (
              <SmartphoneIcon className="size-3.5" />
            ) : (
              <MonitorIcon className="size-3.5" />
            )}
          </div>
          <div className="grid min-w-0 leading-tight">
            <span className="flex items-center gap-1.5 truncate text-sm font-medium">
              {browser}
              {isCurrent && <Badge variant="flat">This device</Badge>}
            </span>
            {activity?.deviceType && (
              <span className="truncate text-xs text-muted-foreground">
                {activity.deviceType}
              </span>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        <div className="grid leading-tight">
          <span>{location || "—"}</span>
          {activity?.ipAddress && (
            <span className="font-mono text-[10px] opacity-70">{activity.ipAddress}</span>
          )}
        </div>
      </TableCell>
      <TableCell className="text-xs font-mono text-muted-foreground">
        {session.lastActiveAt.toLocaleString(undefined, {
          dateStyle: "medium",
          timeStyle: "short",
        })}
      </TableCell>
      <TableCell>
        {!isCurrent && (
          <div className="flex justify-end">
            <Button
              variant="destructive-ghost"
              size="xs"
              disabled={revoking !== null}
              onClick={onRevoke}
            >
              {revoking === session.id ? <Spinner /> : <LogOutIcon />}
              Sign out
            </Button>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

/**
 * Add an email and confirm it with a one-time code — or, with `pending`,
 * finish verifying one that was added earlier.
 */
function EmailVerificationDialog({
  open,
  onOpenChange,
  pending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pending: EmailAddressResource | null;
}) {
  const { user } = useUser();
  const [step, setStep] = useState<"enter" | "code">("enter");
  const [address, setAddress] = useState("");
  const [code, setCode] = useState("");
  const [target, setTarget] = useState<EmailAddressResource | null>(null);
  const [busy, setBusy] = useState<"add" | "send" | "verify" | null>(null);
  const createEmail = useReverification((email: string) => {
    if (!user) throw new Error("not_logged_in");
    return user.createEmailAddress({ email });
  });

  const sendCode = async (email: EmailAddressResource) => {
    setBusy("send");
    try {
      await email.prepareVerification({ strategy: "email_code" });
      toast.success(`Code sent to ${email.emailAddress}`);
    } catch (error) {
      toastClerkError(error, "Failed to send the code");
    } finally {
      setBusy(null);
    }
  };

  // Reset on open, not close, so the form doesn't blank while fading out.
  useEffect(() => {
    if (!open) return;
    setAddress("");
    setCode("");
    setTarget(pending);
    setStep(pending ? "code" : "enter");
    if (pending) void sendCode(pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trimmedAddress = address.trim().toLowerCase();
  const addressValid = EMAIL_RE.test(trimmedAddress);

  const handleAdd = async () => {
    if (!addressValid || busy) return;
    setBusy("add");
    try {
      const created = await createEmail(trimmedAddress);
      await created.prepareVerification({ strategy: "email_code" });
      setTarget(created);
      setStep("code");
    } catch (error) {
      toastClerkError(error, "Failed to add the email address");
    } finally {
      setBusy(null);
    }
  };

  const handleVerify = async () => {
    if (!target || !code.trim() || busy) return;
    setBusy("verify");
    try {
      const result = await target.attemptVerification({ code: code.trim() });
      if (result.verification.status === "verified") {
        toast.success(`${result.emailAddress} verified`);
        onOpenChange(false);
      } else {
        toast.error("That code didn't work. Try again or resend it.");
      }
    } catch (error) {
      toastClerkError(error, "That code didn't work");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {step === "enter" ? (
          <>
            <DialogHeader>
              <DialogTitle>Add an email address</DialogTitle>
              <DialogDescription>We'll send a code to confirm it's yours.</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2">
              <Label htmlFor="new_email_address">Email address</Label>
              <Input
                id="new_email_address"
                type="email"
                autoFocus
                placeholder="name@company.com"
                value={address}
                disabled={busy !== null}
                onChange={(e) => setAddress(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void handleAdd();
                }}
              />
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="secondary" disabled={busy !== null}>
                  Cancel
                </Button>
              </DialogClose>
              <Button onClick={() => void handleAdd()} disabled={!addressValid || busy !== null}>
                {busy === "add" ? <Spinner /> : <MailIcon />}
                Send code
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Verify your email</DialogTitle>
              <DialogDescription>
                Enter the code we sent to{" "}
                <span className="font-medium text-foreground">{target?.emailAddress}</span>.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2">
              <Label htmlFor="email_verification_code">Verification code</Label>
              <Input
                id="email_verification_code"
                autoFocus
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                className="font-mono tracking-[0.3em]"
                value={code}
                disabled={busy === "verify"}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void handleVerify();
                }}
              />
              <Button
                variant="link"
                scope={false}
                className="h-auto w-fit p-0 text-xs text-muted-foreground hover:text-foreground"
                disabled={busy !== null || !target}
                onClick={() => target && void sendCode(target)}
              >
                {busy === "send" ? "Sending…" : "Resend code"}
              </Button>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="secondary" disabled={busy === "verify"}>
                  Cancel
                </Button>
              </DialogClose>
              <Button
                onClick={() => void handleVerify()}
                disabled={code.trim().length < 6 || busy !== null}
              >
                {busy === "verify" ? <Spinner /> : <ShieldCheckIcon />}
                Verify
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
